import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../assets/images/deleteIcon.svg';
import { ReactComponent as WordIcon } from '../../assets/images/msWordIcon.svg';
import { ReactComponent as PdfIcon } from '../../assets/images/pdfIcon.svg';
import { ReactComponent as UploadIcon } from '../../assets/images/uploadIcon.svg';
import CustomCheckbox from '../shared/customCheckbox/customCheckbox';

import styles from './FileUpload.module.scss';

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const FileUpload = ({ fieldName, files = [], setFieldValue, onRemove, isRequired, isOptional, error, ...props }) => {
  const { t } = useTranslation('common');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const checkForDuplicates = (newFiles, existingFiles) => {
    const allFiles = [...existingFiles, ...newFiles];
    const fileNames = allFiles.map((file) => file.name.toLowerCase());
    const duplicates = fileNames.filter((name, index) => fileNames.indexOf(name) !== index);

    if (duplicates.length > 0) {
      const uniqueDuplicates = [...new Set(duplicates)];
      return t('fileUpload.duplicateFile', { filename: uniqueDuplicates[0] });
    }
    return '';
  };

  const processFiles = (fileList) => {
    const selected = Array.from(fileList);
    const validFiles = selected.filter((file) => {
      const isValid = ALLOWED_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;
      // eslint-disable-next-line no-console
      if (!isValid) console.error(`Invalid file: ${file.name}`);
      return isValid;
    });

    // Check for duplicates
    const duplicateError = checkForDuplicates(validFiles, files);
    if (duplicateError) {
      setErrorMessage(duplicateError);
      return;
    }

    // Clear any previous error messages
    setErrorMessage('');

    if (validFiles.length > 0) {
      const newFiles = [...files, ...validFiles];
      newFiles[newFiles.length - 1].is_selected = true;
      setFieldValue(fieldName, newFiles);
    }
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = e.dataTransfer.files;
    processFiles(droppedFiles);
  };

  const handleRemove = (index) => {
    const fileToRemove = files[index];

    const updated = [...files];
    updated.splice(index, 1);
    setFieldValue(fieldName, updated);

    if (fileToRemove.id) {
      onRemove?.(fileToRemove.id);
    }

    setErrorMessage('');
  };

  const getFileIcon = (file) => {
    if (file.type === 'application/pdf' || (file.name && file.name.endsWith('.pdf'))) return <PdfIcon />;
    if (
      (file.type && file.type.includes('word')) ||
      (file.name && (file.name.endsWith('.doc') || file.name.endsWith('.docx')))
    )
      return <WordIcon />;
    return <UploadIcon />;
  };

  const handleCheckboxChange = (index) => {
    const updatedFiles = [...files];
    updatedFiles[index].is_selected = !updatedFiles[index].is_selected;
    setFieldValue(fieldName, updatedFiles);
  };

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor={`${fieldName}-input`}>
        {t('labels.resume')}
        {isRequired && <span className={styles.required}> *</span>}
        {isOptional && <span className={styles.optional}> ({t('optional')})</span>}
      </label>
      <div className={styles.fileList}>
        {files.map((file, i) => (
          <div key={`file-${file.name.replace(/\s+/g, '-').toLowerCase()}`} className={styles.fileItemContainer}>
            <CustomCheckbox checked={file.is_selected} onCheckboxChange={() => handleCheckboxChange(i)} />
            <div className={styles.fileItem}>
              <div className={styles.fileInfo}>
                {getFileIcon(file)}
                <div className={styles.fileDetails}>
                  <div className={styles.fileName}>{file.name}</div>
                  <div className={styles.fileSize}>{file.size ? `${(file.size / 1024).toFixed(0)} KB` : ''}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className={styles.deleteButton}
                aria-label={`Remove ${file.id}`}
              >
                <DeleteIcon />
              </button>
            </div>
          </div>
        ))}

        <label
          className={`${styles.uploadArea} ${isDragOver ? styles.dragOver : ''}`}
          htmlFor={`${fieldName}-input`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.uploadIconContainer}>
            <UploadIcon />
          </div>
          <div>
            <input
              id={`${fieldName}-input`}
              type="file"
              accept=".pdf,.doc,.docx"
              multiple
              onChange={handleFileChange}
              className={styles.fileInput}
              {...props}
            />
            <div className={styles.uploadContent}>
              <p className={styles.uploadHint}>
                <span className={styles.uploadText}>{t('fileUpload.clickToAttach')}</span> {t('fileUpload.orDragDrop')}
                <br />
                {t('fileUpload.pdfOrWord')}
              </p>
            </div>
          </div>
        </label>
      </div>
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      {error && !errorMessage && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};
